import React, { useEffect, useMemo, useState } from "react";
import NativeAnalysis from "../NativeAnalysis";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const SERIES_COLORS = ["#03203d", "#59AD31", "#FB752E"];
const DEFAULT_MAX_LAG_YEARS = 3;
const MIN_POINTS_FOR_CORR = 4;

function formatIndicatorLabel(key) {
  if (!key) return "";
  return String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function axisLabelText(meta) {
  const label = String(meta?.indicator_label || meta?.indicator_key || "").trim();
  const unit = String(meta?.unit || "").trim();
  return unit ? `${label} - ${unit}` : label;
}

function TradeoffTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;

  const sorted = [...payload].sort((a, b) => {
    const av = typeof a.value === "number" ? a.value : Number.NEGATIVE_INFINITY;
    const bv = typeof b.value === "number" ? b.value : Number.NEGATIVE_INFINITY;
    return bv - av;
  });

  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 shadow-md text-[11px]">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">{label}</div>

      {sorted.map((entry) => (
        <div
          key={entry.dataKey}
          className="mb-0.5 flex items-center justify-between gap-2"
        >
          <div className="flex items-center gap-1.5">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-[#7E8081]">{entry.name}</span>
          </div>

          <span className="font-medium text-[#323334]">
            {typeof entry.value === "number"
              ? entry.value.toLocaleString("en-US", { maximumFractionDigits: 2 })
              : "-"}
          </span>
        </div>
      ))}
    </div>
  );
}

function toFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function pearsonCorrelation(xs, ys) {
  const n = Math.min(xs.length, ys.length);
  if (n < 2) return { r: null, n };

  let sumX = 0;
  let sumY = 0;
  for (let i = 0; i < n; i += 1) {
    sumX += xs[i];
    sumY += ys[i];
  }
  const meanX = sumX / n;
  const meanY = sumY / n;

  let num = 0;
  let denX = 0;
  let denY = 0;
  for (let i = 0; i < n; i += 1) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    num += dx * dy;
    denX += dx * dx;
    denY += dy * dy;
  }

  const denom = Math.sqrt(denX * denY);
  if (!denom) return { r: null, n };
  return { r: num / denom, n };
}

function rankWithAverageTies(values) {
  // Returns ranks starting at 1. Ties get the average of their ranks.
  const indexed = values.map((v, i) => ({ v, i }));
  indexed.sort((a, b) => a.v - b.v);

  const ranks = new Array(values.length);
  let pos = 0;
  while (pos < indexed.length) {
    let end = pos + 1;
    while (end < indexed.length && indexed[end].v === indexed[pos].v) end += 1;
    const avgRank = (pos + 1 + end) / 2;
    for (let j = pos; j < end; j += 1) {
      ranks[indexed[j].i] = avgRank;
    }
    pos = end;
  }
  return ranks;
}

function spearmanCorrelation(xs, ys) {
  const rx = rankWithAverageTies(xs);
  const ry = rankWithAverageTies(ys);
  return pearsonCorrelation(rx, ry);
}

function seriesMapByYear(tradeoffRows, key) {
  const map = new Map();
  tradeoffRows.forEach((row) => {
    const yearRaw = row?.year;
    const year = typeof yearRaw === "number" ? yearRaw : Number(yearRaw);
    const v = toFiniteNumber(row?.[key]);
    if (!Number.isFinite(year) || v === null) return;
    map.set(year, v);
  });
  return map;
}

function overlapPairs(mapA, mapB) {
  const years = [];
  const xs = [];
  const ys = [];
  for (const [year, x] of mapA.entries()) {
    const y = mapB.get(year);
    if (typeof y !== "number") continue;
    years.push(year);
    xs.push(x);
    ys.push(y);
  }
  // Sort by year for delta calculations consistency
  const idx = years.map((y, i) => ({ y, i })).sort((a, b) => a.y - b.y);
  const sortedYears = idx.map((o) => o.y);
  const sortedX = idx.map((o) => xs[o.i]);
  const sortedY = idx.map((o) => ys[o.i]);
  return { years: sortedYears, xs: sortedX, ys: sortedY };
}

function deltaPairs(xs, ys) {
  if (xs.length < 2 || ys.length < 2) return { dx: [], dy: [] };
  const dx = [];
  const dy = [];
  for (let i = 1; i < xs.length; i += 1) {
    dx.push(xs[i] - xs[i - 1]);
    dy.push(ys[i] - ys[i - 1]);
  }
  return { dx, dy };
}

function laggedPairs(mapA, mapB, lag) {
  // Correlate A(year) with B(year+lag)
  const xs = [];
  const ys = [];
  for (const [year, x] of mapA.entries()) {
    const y = mapB.get(year + lag);
    if (typeof y !== "number") continue;
    xs.push(x);
    ys.push(y);
  }
  return { xs, ys };
}

function bestLagCorrelation(mapA, mapB, maxLagYears) {
  let bestR = null;
  let bestLag = 0;
  let bestN = 0;

  for (let lag = -maxLagYears; lag <= maxLagYears; lag += 1) {
    const { xs, ys } = laggedPairs(mapA, mapB, lag);
    if (xs.length < MIN_POINTS_FOR_CORR) continue;
    const { r, n } = pearsonCorrelation(xs, ys);
    if (typeof r !== "number") continue;
    const abs = Math.abs(r);
    const bestAbs = typeof bestR === "number" ? Math.abs(bestR) : -1;
    if (abs > bestAbs) {
      bestR = r;
      bestLag = lag;
      bestN = n;
    }
  }

  return { r: bestR, lag: bestLag, n: bestN };
}

export default function TradeoffContent({ resultsData }) {
  const first = resultsData ?? {};
  const [, pathwayObject] = Object.entries(first)[0] ?? [];
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280,
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const tradeoffData = useMemo(() => {
    return Array.isArray(pathwayObject?.tradeoff?.data)
      ? pathwayObject.tradeoff.data
      : [];
  }, [pathwayObject]);

  const indicatorCatalog = useMemo(() => {
    const catalog = Array.isArray(pathwayObject?.tradeoff?.indicatorCatalog)
      ? pathwayObject.tradeoff.indicatorCatalog
      : [];

    if (catalog.length > 0) {
      return catalog.map((item) => ({
        indicator_key: item?.indicator_key,
        indicator_label: item?.indicator_label || formatIndicatorLabel(item?.indicator_key),
        indicator_name: item?.indicator_name || item?.indicator_label || formatIndicatorLabel(item?.indicator_key),
        unit: item?.unit || null,
        indicator_group: item?.indicator_group || "Other",
      }));
    }

    const keys = Array.isArray(pathwayObject?.tradeoff?.availableIndicators)
      ? pathwayObject.tradeoff.availableIndicators
      : [];

    return keys.map((key) => ({
      indicator_key: key,
      indicator_label: formatIndicatorLabel(key),
      unit: null,
      indicator_group: "Other",
    }));
  }, [pathwayObject]);

  const indicatorMetaByKey = useMemo(
    () => new Map(indicatorCatalog.map((item) => [item.indicator_key, item])),
    [indicatorCatalog],
  );

  const groupedIndicatorOptions = useMemo(() => {
    const groups = new Map();

    indicatorCatalog.forEach((item) => {
      const groupName = item.indicator_group || "Other";
      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }

      groups.get(groupName).push({
        value: item.indicator_key,
        label: item.indicator_name || item.indicator_label || formatIndicatorLabel(item.indicator_key),
      });
    });

    return Array.from(groups.entries()).map(([group, options]) => ({
      group,
      options,
    }));
  }, [indicatorCatalog]);

  const [selectedIndicators, setSelectedIndicators] = useState([]);

  useEffect(() => {
    const defaults = indicatorCatalog.slice(0, 3).map((o) => o.indicator_key);
    setSelectedIndicators(defaults);
  }, [indicatorCatalog]);

  const activeIndicators = useMemo(
    () => selectedIndicators.filter(Boolean).slice(0, 3),
    [selectedIndicators],
  );

  const setIndicatorAt = (index, value) => {
    setSelectedIndicators((prev) => {
      const next = [...prev];

      if (!value) {
        next[index] = "";
        return next;
      }

      if (next.some((v, i) => i !== index && v === value)) {
        return prev;
      }

      next[index] = value;
      return next;
    });
  };

  const rightAxisCount = (selectedIndicators[1] ? 1 : 0) + (selectedIndicators[2] ? 1 : 0);
  const isMobile = viewportWidth < 768;
  const isTablet = viewportWidth >= 768 && viewportWidth < 1024;
  const axisLayoutByKey = useMemo(() => {
    const layout = new Map();
    const maxWidth = isMobile ? 84 : isTablet ? 92 : 104;
    const minWidth = isMobile ? 44 : 52;

    // 1) Estimate each axis width from the largest rendered number.
    selectedIndicators.forEach((key) => {
      if (!key) return;
      const numericValues = tradeoffData
        .map((row) => row?.[key])
        .filter((v) => typeof v === "number" && Number.isFinite(v));

      const maxFormattedLen = numericValues.length
        ? Math.max(
            ...numericValues.map(
              (v) =>
                Math.abs(v).toLocaleString("en-US", {
                  maximumFractionDigits: 2,
                }).length,
            ),
          )
        : 4;

      const width = Math.min(maxWidth, Math.max(minWidth, 14 + maxFormattedLen * 7));
      layout.set(key, { width, maxFormattedLen });
    });

    // 2) Derive axis/text offsets from computed widths.
    const CHAR_WIDTH = isMobile ? 6 : 7; // px per character at current font size
    const GAP = 40; // extra separation between right axis 1 and right axis 2
    selectedIndicators.forEach((key, index) => {
      if (!key) return;
      const { width, maxFormattedLen } = layout.get(key) || { width: minWidth, maxFormattedLen: 4 };
      const isRightAxis = index > 0;
      const isSecondRight = index === 1;

      // Half the tick text width = how far the label needs to shift to align with the axis line
      const tickTextHalfWidth = Math.round((maxFormattedLen * CHAR_WIDTH) / 2);

      // Each slot has a different anchor point:
      //   slot 0 (left, insideLeft):  shift left toward axis line
      //   slot 1 (right 1, insideRight): paddedWidth includes GAP, compensate so label stays near real axis line
      //   slot 2 (right 2, insideRight): normal right axis, shift right toward its own axis line
      let labelDx;
      if (index === 0) {
        labelDx = -tickTextHalfWidth;
      } else if (index === 1) {
        const hasThird = Boolean(selectedIndicators[2]);
        // With GAP: label anchor is at right edge of paddedWidth, pull back by GAP
        // Without GAP: same as a normal right axis
        labelDx = hasThird ? tickTextHalfWidth - width : tickTextHalfWidth;
      } else {
        // Cap so label never drifts beyond the axis width
        labelDx = Math.min(tickTextHalfWidth, Math.round(width * 0.1));
      }

      // Add gap to slot 1 so it pushes slot 2 further right
      const paddedWidth = isSecondRight && selectedIndicators[2] ? width + GAP : width;
      const axisDx = 0;
      const tickDx = 0;

      layout.set(key, { width: paddedWidth, axisDx, tickDx, labelDx });
    });

    return layout;
  }, [selectedIndicators, tradeoffData, isMobile, isTablet]);

  const chartMargin = {
    top: 32,
    right: 110,
    left: 35,
    bottom: 24,
  };

  const [analysis, setAnalysis] = useState("");
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  const statExpertPayload = useMemo(() => {
    const keys = activeIndicators;
    if (!Array.isArray(keys) || keys.length < 2) return null;
    if (!Array.isArray(tradeoffData) || tradeoffData.length < 2) return null;

    const dataset_names = keys.map((key) => {
      const meta = indicatorMetaByKey.get(key);
      const name =
        meta?.indicator_name ||
        meta?.indicator_label ||
        formatIndicatorLabel(key);
      const unit = String(meta?.unit || "").trim();
      return unit ? `${name} (${unit})` : name;
    });

    const maps = keys.map((k) => seriesMapByYear(tradeoffData, k));
    const yearsAll = tradeoffData
      .map((row) => (typeof row?.year === "number" ? row.year : Number(row?.year)))
      .filter((y) => Number.isFinite(y));
    const yearMin = yearsAll.length ? Math.min(...yearsAll) : null;
    const yearMax = yearsAll.length ? Math.max(...yearsAll) : null;

    const k = keys.length;
    const initMatrix = (diagValue) =>
      Array.from({ length: k }, (_, i) =>
        Array.from({ length: k }, (_, j) => (i === j ? diagValue : null)),
      );

    const pearson = initMatrix(1);
    const spearman = initMatrix(1);
    const deltaPearson = initMatrix(1);
    const lagBestR = initMatrix(1);
    const lagBest = initMatrix(0);
    const nMatrix = initMatrix(null);
    const nDeltaMatrix = initMatrix(null);
    const nLagMatrix = initMatrix(null);

    for (let i = 0; i < k; i += 1) {
      for (let j = i + 1; j < k; j += 1) {
        const { xs, ys } = overlapPairs(maps[i], maps[j]);

        if (xs.length >= MIN_POINTS_FOR_CORR) {
          const p = pearsonCorrelation(xs, ys);
          const s = spearmanCorrelation(xs, ys);
          pearson[i][j] = p.r;
          pearson[j][i] = p.r;
          spearman[i][j] = s.r;
          spearman[j][i] = s.r;
          nMatrix[i][j] = p.n;
          nMatrix[j][i] = p.n;

          const { dx, dy } = deltaPairs(xs, ys);
          if (dx.length >= MIN_POINTS_FOR_CORR) {
            const dp = pearsonCorrelation(dx, dy);
            deltaPearson[i][j] = dp.r;
            deltaPearson[j][i] = dp.r;
            nDeltaMatrix[i][j] = dp.n;
            nDeltaMatrix[j][i] = dp.n;
          }
        } else {
          nMatrix[i][j] = xs.length;
          nMatrix[j][i] = xs.length;
        }

        const lagRes = bestLagCorrelation(maps[i], maps[j], DEFAULT_MAX_LAG_YEARS);
        if (typeof lagRes.r === "number") {
          lagBestR[i][j] = lagRes.r;
          lagBest[j][i] = -lagRes.lag;
          nLagMatrix[i][j] = lagRes.n;
          nLagMatrix[j][i] = lagRes.n;

          // Lag convention: LagBest[i][j] is lag applied to i vs j (A(year) vs B(year+lag))
          lagBest[i][j] = lagRes.lag;
          lagBestR[j][i] = lagRes.r;
        }
      }
    }

    return {
      context: "tradeoff_relationships",
      dataset_names,
      matrices: {
        Pearson: pearson,
        Spearman: spearman,
        DeltaPearson: deltaPearson,
        LagBestR: lagBestR,
        LagBest: lagBest,
      },
      meta: {
        years: yearMin !== null && yearMax !== null ? [yearMin, yearMax] : null,
        n_matrix: nMatrix,
        n_delta_matrix: nDeltaMatrix,
        n_lag_matrix: nLagMatrix,
        max_lag_years: DEFAULT_MAX_LAG_YEARS,
      },
    };
  }, [activeIndicators, indicatorMetaByKey, tradeoffData]);

  useEffect(() => {
    const payload = statExpertPayload;
    if (!payload) {
      setAnalysis("");
      setLoadingAnalysis(false);
      return;
    }

    let alive = true;
    const controller = new AbortController();

    const run = async () => {
      setLoadingAnalysis(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || ""}/api/chat/statexpert-tradeoff`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            signal: controller.signal,
          },
        );
        if (!response.ok) throw new Error("StatExpert API error");
        const resData = await response.json();
        const text =
          typeof resData?.analysis === "string" ? resData.analysis : "";
        if (alive) setAnalysis(text || "No analysis available.");
      } catch (e) {
        if (!alive) return;
        if (String(e?.name) === "AbortError") return;
        console.error("Error fetching StatExpert analysis:", e);
        setAnalysis("Cannot load analysis at this time.");
      } finally {
        if (alive) setLoadingAnalysis(false);
      }
    };

    run();
    return () => {
      alive = false;
      controller.abort();
    };
  }, [statExpertPayload]);

  return (
    <div className="w-full rounded-xl bg-white overflow-hidden">
      <div className="border-b border-[#E3E5E6] px-8 py-4">
        <h3 className="text-[16px] font-semibold text-[#101010]">Trade-offs Analysis</h3>
        <p className="mt-1 text-sm text-[#7E8081]">
          Select up to three descriptive variables of the pathway. Each indicator is plotted with its own Y axis.
        </p>
      </div>

      <div className="px-8 py-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 px-0">
          {[0, 1, 2].map((idx) => {
            const currentValue = selectedIndicators[idx] || "";
            const groupedOptions = groupedIndicatorOptions
              .map((group) => ({
                group: group.group,
                options: group.options.filter(
                  (opt) =>
                    opt.value === currentValue ||
                    !activeIndicators.includes(opt.value),
                ),
              }))
              .filter((group) => group.options.length > 0);

            return (
              <div key={idx} className="w-full">
                <label className="text-[12px] text-[#686873] block pb-1">
                  Indicator {idx + 1}
                </label>
                <select
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[14px] text-slate-900 shadow-sm outline-none hover:border-slate-300"
                  value={currentValue}
                  onChange={(e) => setIndicatorAt(idx, e.target.value)}
                >
                  <option value="">None</option>
                  {groupedOptions.map((group) => (
                    <optgroup key={group.group} label={group.group}>
                      {group.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            );
          })}
        </div>

        {tradeoffData.length === 0 ? (
          <div className="text-sm text-[#7E8081]">No indicator data available.</div>
        ) : activeIndicators.length === 0 ? (
          <div className="text-sm text-[#7E8081]">Choose at least one indicator to draw the chart.</div>
        ) : (
          <>
            <div className="h-[460px] w-[calc(100%+4rem)]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={tradeoffData}
                  margin={chartMargin}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#EDF0F2" />
                  <XAxis
                    dataKey="year"
                    tick={{ fill: "#7E8081", fontSize: 12 }}
                    tickLine={{ stroke: "#C8CDD2" }}
                    axisLine={{ stroke: "#C8CDD2" }}
                    label={{ value: "YEARS", position: "insideBottom", offset: -4, style: { fill: "#7E8081", fontSize: 12, fontWeight: 600 } }}
                  />

                  {[0, 1, 2].map((slotIndex) => {
                    const key = selectedIndicators[slotIndex];
                    const orientation = slotIndex === 0 ? "left" : "right";
                    const labelFontSize = isMobile ? 10 : 14;

                    if (!key) {
                      // Render a hidden placeholder axis to keep all 3 positions stable
                      return (
                        <YAxis
                          key={`yaxis-${slotIndex}`}
                          yAxisId={`yaxis-${slotIndex}`}
                          orientation={orientation}
                          width={0}
                          tick={false}
                          axisLine={false}
                          tickLine={false}
                        />
                      );
                    }

                    const axisLayout = axisLayoutByKey.get(key) || {
                      width: orientation === "left" ? 60 : 52,
                      axisDx: 0,
                      tickDx: 0,
                      labelDx: -8,
                    };
                    const meta = indicatorMetaByKey.get(key);
                    const yAxisTitle = axisLabelText(meta);

                    return (
                      <YAxis
                        key={`yaxis-${slotIndex}`}
                        yAxisId={`yaxis-${slotIndex}`}
                        orientation={orientation}
                        dx={axisLayout.axisDx}
                        width={axisLayout.width}
                        tick={{
                          fill: SERIES_COLORS[slotIndex],
                          fontSize: labelFontSize,
                          dx: axisLayout.tickDx,
                        }}
                        axisLine={{ stroke: SERIES_COLORS[slotIndex] }}
                        tickLine={{ stroke: SERIES_COLORS[slotIndex] }}
                        tickMargin={3}
                        allowDataOverflow={false}
                        label={{
                          value: yAxisTitle,
                          angle: -90,
                          position: orientation === "left" ? "insideLeft" : "insideRight",
                          dx: axisLayout.labelDx,
                          style: {
                            fill: SERIES_COLORS[slotIndex],
                            fontSize: labelFontSize,
                            fontWeight: 600,
                            textAnchor: "middle",
                            dominantBaseline: "middle",
                          },
                        }}
                      />
                    );
                  })}

                  <Tooltip content={<TradeoffTooltip />} />
                  <Legend
                    verticalAlign="top"
                    wrapperStyle={{ fontSize: 16, color: "#98999A" }}
                    formatter={(value) => {
                      const meta = indicatorMetaByKey.get(value);
                      return meta?.indicator_name || meta?.indicator_label || formatIndicatorLabel(value);
                    }}
                  />

                  {selectedIndicators.map((key, slotIndex) => !key ? null : (
                    <Line
                      key={key}
                      type="monotone"
                      yAxisId={`yaxis-${slotIndex}`}
                      dataKey={key}
                      name={(() => { const m = indicatorMetaByKey.get(key); return m?.indicator_name || m?.indicator_label || formatIndicatorLabel(key); })()}
                      stroke={SERIES_COLORS[slotIndex]}
                      strokeWidth={3}
                      dot={false}
                      connectNulls
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="w-full min-h-[100px] mt-4">
              {activeIndicators.length < 2 ? (
                <div className="text-sm text-[#7E8081]">
                  Select at least two indicators to generate a relationship summary.
                </div>
              ) : loadingAnalysis ? (
                <div className="flex items-center gap-2 text-[#7E8081] text-sm md:text-base animate-pulse">
                  <svg
                    className="animate-spin h-5 w-5 text-[#59AD31]"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    ></path>
                  </svg>
                  <span className="font-bold">Generating analysis...</span>
                </div>
              ) : (
                <div className="group relative">
                  <NativeAnalysis content={analysis} />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
